import React, { useEffect } from 'react';
import { ArrowRight, Target, Zap, TrendingUp, Globe, Users, BarChart3, Megaphone, Palette, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

export const AboutPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const services = [
    { icon: Target, title: 'Digital Marketing Strategy', desc: 'Custom growth frameworks designed around business objectives, not trends.' },
    { icon: Megaphone, title: 'Paid Advertising', desc: 'Performance campaigns engineered for ROI and scalable acquisition.' },
    { icon: Globe, title: 'Website Development', desc: 'Custom-built presentation and conversion websites designed to sell.' },
    { icon: Users, title: 'Social Media Positioning', desc: 'Authority-driven content strategies that transform brands into market leaders.' },
    { icon: Palette, title: 'Brand Architecture', desc: 'Clarity, identity, positioning, tone — engineered to influence perception.' },
  ];

  const clientTypes = [
    'Ambitious startups',
    'Established SMEs',
    'Premium local businesses',
    'International growth-focused companies'
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto pt-32">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-pink-400 to-blue-600">
              WORLDWIDE INFLUENCE MEDIA
            </span>
          </h1>
          <p className="text-2xl sm:text-3xl md:text-4xl text-gray-300 font-light mb-4">
            We Don't Just Market Brands.
          </p>
          <p className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-blue-400">
            We Build Influence.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 md:p-12">
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              <span className="text-white font-semibold">WORLDWIDE INFLUENCE MEDIA</span> is a strategic digital marketing agency built for brands that refuse to be average.
            </p>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
              We operate at the intersection of <span className="text-pink-400">performance</span>, <span className="text-blue-400">positioning</span>, and <span className="text-pink-400">persuasion</span> — helping businesses grow through intelligent marketing systems, high-converting websites, and authority-driven content.
            </p>
            <div className="border-l-4 border-pink-500 pl-6 my-8">
              <p className="text-xl md:text-2xl text-white italic">
                We are not a "post and hope" agency.
              </p>
              <p className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-blue-400 mt-2">
                We engineer growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-16">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-blue-400">Philosophy</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              { title: 'Attention', subtitle: 'is currency.' },
              { title: 'Trust', subtitle: 'is leverage.' },
              { title: 'Influence', subtitle: 'is power.' },
            ].map((item, index) => (
              <div key={index} className="text-center p-8 bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-pink-500/50 rounded-2xl transition-all duration-300 hover:transform hover:scale-105">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-xl text-pink-400">{item.subtitle}</p>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto space-y-6 text-center">
            <p className="text-lg md:text-xl text-gray-300">
              In a world oversaturated with noise, we focus on <span className="text-white font-semibold">clarity</span>.
            </p>
            <p className="text-lg md:text-xl text-gray-300">
              In markets crowded with competitors, we build <span className="text-white font-semibold">positioning</span>.
            </p>
            <p className="text-lg md:text-xl text-gray-300">
              In campaigns obsessed with vanity metrics, we deliver <span className="text-white font-semibold">measurable results</span>.
            </p>
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {['Performance marketing', 'Strategic branding', 'High-converting web development', 'Advanced paid media systems', 'Data-driven scaling frameworks'].map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <CheckCircle className="text-pink-400 flex-shrink-0" size={20} />
                <span className="text-gray-300 text-sm">{item}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-xl md:text-2xl text-white font-semibold">
              We don't guess. <span className="text-pink-400">We test, optimize, and scale.</span>
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-8">
            What We <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-blue-400">Actually Do</span>
          </h2>
          
          <p className="text-center text-xl text-gray-300 mb-16">
            We help companies move from:
          </p>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-16">
            {[
              { from: 'Unknown', to: 'Recognized' },
              { from: 'Recognized', to: 'Trusted' },
              { from: 'Trusted', to: 'Dominant' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 text-lg md:text-xl">
                <span className="text-gray-400">{item.from}</span>
                <ArrowRight className="text-pink-400" size={24} />
                <span className="text-white font-semibold">{item.to}</span>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="group p-6 bg-slate-900/50 backdrop-blur-sm border border-slate-800 hover:border-pink-500/50 rounded-2xl transition-all duration-300 hover:transform hover:-translate-y-2">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="text-pink-400" size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-gray-400">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built For Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-12">
            Built for <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-blue-400">Serious Brands</span>
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {clientTypes.map((type, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                <CheckCircle className="text-pink-400 flex-shrink-0" size={20} />
                <span className="text-gray-300">{type}</span>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-pink-500/10 to-blue-500/10 border border-pink-500/30 rounded-2xl p-8 md:p-12">
            <p className="text-xl text-gray-300 mb-4">
              If you want cheap marketing, <span className="text-gray-500">we're not the right fit.</span>
            </p>
            <p className="text-2xl text-white font-semibold">
              If you want strategic growth, <span className="text-pink-400">you're exactly where you need to be.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Why Clients Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center mb-12">
            Why Clients <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-blue-400">Choose Us</span>
          </h2>

          <div className="text-center mb-12">
            <p className="text-2xl text-white font-semibold mb-8">Because we think like business owners.</p>
            <p className="text-xl text-gray-300 mb-6">We understand:</p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {['Margins', 'Cash flow', 'Customer acquisition costs', 'Lifetime value', 'Scalability'].map((item, index) => (
                <span key={index} className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-full text-gray-300">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="border-l-4 border-blue-500 pl-6">
            <p className="text-xl md:text-2xl text-white">
              Marketing is not art for us.
            </p>
            <p className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-pink-400 mt-2">
              It is controlled expansion.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 md:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                Our <span className="text-pink-400">Mission</span>
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <Zap className="text-pink-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">To build brands that <span className="text-white">dominate markets</span> — not follow them.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="text-pink-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">To transform marketing from <span className="text-white">expense into asset</span>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Zap className="text-pink-400 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-300">To create systems that generate <span className="text-white">influence at scale</span>.</span>
                </li>
              </ul>
            </div>

            {/* Vision */}
            <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-8 md:p-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                The <span className="text-blue-400">Vision</span>
              </h2>
              <p className="text-gray-300 mb-6">
                <span className="text-white font-semibold">WORLDWIDE INFLUENCE MEDIA</span> is built to operate globally.
              </p>
              <p className="text-gray-300">
                We aim to become a strategic growth partner for brands across Europe and beyond — delivering <span className="text-white">measurable impact</span>, not empty impressions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Statement */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="space-y-6">
            <p className="text-xl md:text-2xl text-gray-400">
              This is not about ads. <span className="text-white font-semibold">This is about leverage.</span>
            </p>
            <p className="text-xl md:text-2xl text-gray-400">
              This is not about visibility. <span className="text-white font-semibold">This is about authority.</span>
            </p>
            <p className="text-xl md:text-2xl text-gray-400">
              This is not about marketing. <span className="text-white font-semibold">This is about influence.</span>
            </p>
          </div>

          <div className="mt-16">
            <Button
              onClick={() => navigate('/')}
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-blue-500 hover:from-pink-600 hover:to-blue-600 text-white px-10 py-6 text-lg font-semibold rounded-xl shadow-2xl hover:shadow-pink-500/30 transition-all duration-300"
            >
              Start Your Journey
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
